import { HexColorPicker } from 'react-colorful';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, Dialog } from '~/components';
import { Field, Form, Input } from '~/components/Form';
import { maxImageUploadSize } from '~/constants/constants';
import { EMAIL } from '~/constants/regex';
import { isNotBlank } from '~/utils/string';
import { toast } from '~/utils/toast';

import type { CreateEditBrandFormType, CreateEditBrandModalProps as Props } from './types';

import { ImageUploadField } from '../../../components/ImageUploadField';
import { BrandLogoInput } from './BrandLogoInput';
import { colorPickerStyle, fieldsStyle } from './CreateEditBrandModal.css';

const getRandomHexColor = () =>
  `#${Math.floor(Math.random() * 16777216)
    .toString(16)
    .padStart(6, '0')}`;

export const CreateEditBrandModal = ({ brand, isLoading, onClose, onSubmit }: Props) => {
  const { control, handleSubmit } = useForm<CreateEditBrandFormType>({
    defaultValues: {
      companyName: brand?.companyName ?? '',
      email: brand?.email ?? '',
      color: brand?.color ?? getRandomHexColor(),
    },
  });

  const { t } = useTranslation(['common', 'settings', 'validation']);

  return (
    <Dialog.Root onOpenChange={(open) => !open && onClose()} open>
      <Dialog.Body>
        <Dialog.Header title={t(`settings:profile.brands.${brand ? 'edit' : 'add'}`)} />

        <Form onSubmit={handleSubmit(onSubmit)}>
          <div className={fieldsStyle}>
            <Controller
              control={control}
              name="companyName"
              render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
                <Field.Root invalid={invalid} name={name}>
                  <Field.Label>{t('settings:fields.brandName.label')}</Field.Label>

                  <Input
                    onBlur={onBlur}
                    onValueChange={onChange}
                    placeholder={t('settings:fields.brandName.placeholder')}
                    ref={ref}
                    value={value}
                  />

                  <Field.Error match={!!error}>{error?.message}</Field.Error>
                </Field.Root>
              )}
              rules={{
                required: { value: true, message: t('validation:required') },
                validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
                maxLength: {
                  value: 255,
                  message: t('validation:maxLength', { attribute: t('settings:fields.brandName.label'), max: 255 }),
                },
              }}
            />

            <Controller
              control={control}
              name="email"
              render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
                <Field.Root invalid={invalid} name={name}>
                  <Field.Label>{t('settings:fields.email.label')}</Field.Label>

                  <Input
                    onBlur={onBlur}
                    onValueChange={onChange}
                    placeholder={t('settings:fields.email.placeholder')}
                    ref={ref}
                    value={value}
                  />

                  <Field.Error match={!!error}>{error?.message}</Field.Error>
                </Field.Root>
              )}
              rules={{
                required: { value: true, message: t('validation:required') },
                validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
                maxLength: {
                  value: 255,
                  message: t('validation:maxLength', { attribute: t('settings:fields.email.label'), max: 255 }),
                },
                pattern: { value: EMAIL, message: t('validation:email.invalid') },
              }}
            />

            <Controller
              control={control}
              name="color"
              render={({ field: { onBlur, onChange, value }, fieldState: { error, invalid } }) => (
                <Field.Root invalid={invalid}>
                  <Field.Label>{t('settings:fields.color.label')}</Field.Label>

                  <HexColorPicker
                    aria-label={t('settings:fields.color.label')}
                    className={colorPickerStyle}
                    color={value}
                    onBlur={onBlur}
                    onChange={onChange}
                  />

                  <Field.Error match={!!error}>{error?.message}</Field.Error>
                </Field.Root>
              )}
              rules={{ required: { value: true, message: t('validation:required') } }}
            />

            {!brand ? (
              <Controller
                control={control}
                name="logo"
                render={({ field: { onChange, value } }) => (
                  <ImageUploadField
                    actionsLayout="row"
                    deleteLabel={t('settings:imageUpload.logo.delete')}
                    description={t('settings:fields.logo.tooltip')}
                    isDeleting={false}
                    isUploading={false}
                    label={t('settings:fields.logo.label')}
                    onDelete={() => onChange(undefined)}
                    onUpload={(file) => {
                      if (file.size > maxImageUploadSize) {
                        return toast.danger({ description: t('settings:fields.logo.error.tooLarge') });
                      }

                      onChange(file);
                    }}
                    pendingFile={value}
                    uploadLabel={t('settings:imageUpload.logo.upload')}
                    url={null}
                    variant="logo"
                  />
                )}
              />
            ) : (
              <BrandLogoInput brand={brand} description={t('settings:fields.logo.tooltip')} />
            )}
          </div>

          <Dialog.Actions>
            <Button icon="Save" loading={isLoading} type="submit">
              {t('common:save')}
            </Button>
          </Dialog.Actions>
        </Form>
      </Dialog.Body>
    </Dialog.Root>
  );
};
